import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Form, Input, Button, message, Typography, Checkbox, Divider, Radio } from 'antd';
import { Link } from 'react-router';
import { SIGN_UP } from '../../graphql/mutations/mutations';
import { useAuth } from '../../providers/AuthProvider';
import { RegisterFormValues } from '../../types/auth';
import { AuthWithSocial } from './AuthWithSocial';
import { useAuthLayoutContext } from '../../layouts/AuthLayout';
import TagManager from 'react-gtm-module';

const { Title, Text } = Typography;

const EmailSent: React.FC<{ email: string }> = ({ email }) => {
  return (
    <div>
      <Title level={3}>Check your email</Title>
      <p>We&apos;ve sent a confirmation email to {email}</p>
      <Link to="/login">
        <Button type="primary" size="large" block>
          Go to login
        </Button>
      </Link>
    </div>
  );
};

export const Register: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>();
  const { setAuthUser } = useAuth();
  const selectedRole = Form.useWatch('role', form);
  const { isMobile, setMobileView } = useAuthLayoutContext();

  const [signUp] = useMutation(SIGN_UP, {
    onCompleted: data => {
      if (data?.signUp?.email) {
        message.success('Registration successful! Please check your email.');
        setRegisteredEmail(form.getFieldValue('email'));
      }
      setLoading(false);
    },
    onError: error => {
      const errors = error.graphQLErrors;
      errors.forEach(err => {
        if (err.message.includes('email')) {
          form.setFields([
            {
              name: 'email',
              errors: ['Email has already been taken'],
            },
          ]);
        }
      });
      message.error('Registration failed');
      setLoading(false);
    },
  });

  const handleSubmit = (values: RegisterFormValues) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'register_submit',
        page: '/register',
        role: values.role,
      },
    });
    setLoading(true);
    signUp({
      variables: {
        email: values.email,
        password: values.password,
        name: values.name,
        roles: [values.role],
      },
    });
  };

  if (registeredEmail) {
    return <EmailSent email={registeredEmail} />;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        terms: false,
        role: 'USER',
      }}>
      <Title level={3}>Create your account</Title>

      {selectedRole !== 'CONTRACTOR' && (
        <>
          <AuthWithSocial
            onComplete={setAuthUser}
            onLoading={setLoading}
            successMessage="Registration successful."
            errorMessage="Registration failed"
            type="register"
            loading={loading}
          />
          <Divider plain>or</Divider>
        </>
      )}

      <Form.Item
        name="name"
        label="First Name"
        rules={[{ required: true, message: 'Please enter your name' }]}>
        <Input placeholder="How you like to be addressed" size="large" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: 'Please enter your email' },
          { type: 'email', message: 'Please enter a valid email' },
        ]}>
        <Input placeholder="your@email.com" size="large" />
      </Form.Item>

      <Form.Item
        name="password"
        label="Password"
        rules={[
          { required: true, message: 'Please enter your password' },
          { min: 8, message: 'Password must be at least 8 characters' },
          {
            pattern: /[0-9]/,
            message: 'Password must contain at least one number',
          },
          {
            pattern: /[a-z]/,
            message: 'Password must contain at least one lowercase letter',
          },
          {
            pattern: /[A-Z]/,
            message: 'Password must contain at least one uppercase letter',
          },
        ]}>
        <Input.Password placeholder="Your password" size="large" />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        label="Confirm Password"
        dependencies={['password']}
        rules={[
          { required: true, message: 'Please confirm your password' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Passwords do not match'));
            },
          }),
        ]}>
        <Input.Password placeholder="Confirm password" size="large" />
      </Form.Item>

      <Form.Item
        name="role"
        label="Account Type"
        rules={[{ required: true, message: 'Please select account type' }]}>
        <Radio.Group
          onChange={e =>
            TagManager.dataLayer({
              dataLayer: {
                event: 'register_role_select',
                page: '/register',
                role: e.target.value,
              },
            })
          }>
          <Radio.Button value="USER">Homeowner</Radio.Button>
          <Radio.Button value="CONTRACTOR">Contractor</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="terms"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) =>
              value
                ? Promise.resolve()
                : Promise.reject(new Error('Please accept the terms and conditions')),
          },
        ]}>
        <Checkbox>
          I agree to{' '}
          <a href="https://homewip.com/terms-conditions/" target="_blank" rel="noopener noreferrer">
            Terms
          </a>{' '}
          and{' '}
          <a href="https://homewip.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
        </Checkbox>
      </Form.Item>

      <Button type="primary" htmlType="submit" size="large" block loading={loading}>
        Create account
      </Button>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Text>
          Already have an account? <Link to="/login">Log in</Link>
        </Text>
      </div>

      {isMobile && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <Button type="link" onClick={() => setMobileView('landing')}>
            Back
          </Button>
        </div>
      )}
    </Form>
  );
};
